/**
 * The decisions behind the web client's sounds — pure functions, so `bun test` can pin them the way
 * `callRules.test.ts` pins the call state machine, and so the browser and the native client run one
 * rule set rather than two (these are `apps/android-native/.../sound/SoundRules.kt`, word for word).
 *
 * The rules that matter: a blip never announces your own echo, the thread you are reading, a muted
 * chat, or a hidden tab; a call that is being answered goes quiet rather than beeping; and only a
 * call that was actually up earns an end tone.
 */

/** The small tones the app makes about its own state — one vocabulary both clients share. */
export type AppBlip = 'message' | 'sent' | 'join' | 'leave' | 'end'

/**
 * Whether a chat's mute window silences it right now: `mutedUntil` is null (unmuted), -1 (forever),
 * or a deadline in epoch ms (docs/CONTRACT.md §4). The same rule the native client runs.
 */
export function chatMuted(mutedUntil: number | null | undefined, now: number = Date.now()): boolean {
  if (mutedUntil == null) return false
  return mutedUntil === -1 || mutedUntil > now
}

export type MessageBlipInput = {
  /** The socket echoes your own message back so the optimistic bubble can reconcile; not an arrival. */
  mine: boolean
  muted: boolean
  /** The chat whose thread is on screen — a blip must not announce what is already being read. */
  openChatId: string | null
  chatId: string
  /** `document.visibilityState === 'visible'`: a hidden tab has the push notification's own sound. */
  visible: boolean
}

export function messageBlip(input: MessageBlipInput): boolean {
  return !input.mine && !input.muted && input.openChatId !== input.chatId && input.visible
}

/**
 * The first remote participant. While the caller is still waiting to be answered this is silence:
 * the ringback simply stops, which is the connected signal a phone call gives. Once the call has
 * been up, arrivals get the tone.
 */
export function joinBlip(awaitingAnswer: boolean): AppBlip | null {
  return awaitingAnswer ? null : 'join'
}

/**
 * A participant left, with `remainingRemotes` others still in the room. Zero is silence: the last
 * departure is the call ending, and that has its own tone (`endBlip`) — one sound per event, not two.
 */
export function leaveBlip(remainingRemotes: number): AppBlip | null {
  return remainingRemotes > 0 ? 'leave' : null
}

/**
 * A call that is over. Only a call that was actually up earns a tone: a connection that failed, or
 * a ring that died before being answered, just stops.
 */
export function endBlip(wasConnected: boolean): AppBlip | null {
  return wasConnected ? 'end' : null
}
